import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Inbox,
  Newspaper,
  LayoutTemplate,
  Image as ImageIcon,
  Bell,
  Share2,
  BarChart3,
  Mail,
  Receipt,
  LogOut,
  ExternalLink,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/billing", label: "Rechnungen", icon: Receipt },
  { to: "/admin/blog", label: "Magazin", icon: Newspaper },
  { to: "/admin/site", label: "Homepage", icon: LayoutTemplate },
  { to: "/admin/media", label: "Mediathek", icon: ImageIcon },
  { to: "/admin/push", label: "Push", icon: Bell },
  { to: "/admin/og", label: "Social Cards", icon: Share2 },
  { to: "/admin/email", label: "E-Mail", icon: Mail },
];

const AdminShell = ({ title, subtitle, actions, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#fafaf7] text-neutral-900" data-testid="admin-shell">
      {/* Sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-neutral-200 flex-col z-30">
        <div className="px-6 py-6 border-b border-neutral-100">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-sidebar-logo">
            <div className="w-9 h-9 rounded-xl bg-neutral-950 text-white grid place-items-center font-display font-semibold">
              M
            </div>
            <div className="leading-tight">
              <div className="font-display text-[15px] font-medium">Mallorca Digital</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-sky-700">Admin</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-neutral-950 text-white"
                    : "text-neutral-700 hover:bg-neutral-100"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-5">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-neutral-700 hover:bg-neutral-100"
          >
            <ExternalLink className="w-4 h-4" />
            Website öffnen
          </a>
          <div className="mt-3 px-3 py-3 rounded-2xl bg-neutral-50 border border-neutral-100">
            <div className="text-xs text-neutral-500">Angemeldet als</div>
            <div className="text-sm font-medium truncate">{user?.email}</div>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-neutral-950 text-white px-3 py-2 text-xs hover:bg-black"
            >
              <LogOut className="w-3.5 h-3.5" />
              Abmelden
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <MobileTopBar onLogout={onLogout} />

      <main className="lg:pl-64">
        <div className="container-px mx-auto max-w-6xl py-8 lg:py-10 px-6">
          {(title || actions) && (
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
              <div>
                {title && (
                  <h1
                    className="font-display text-3xl lg:text-4xl font-light tracking-tight text-neutral-950"
                    data-testid="admin-page-title"
                  >
                    {title}
                  </h1>
                )}
                {subtitle && (
                  <p className="mt-1.5 text-sm text-neutral-500 max-w-2xl">{subtitle}</p>
                )}
              </div>
              {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

const MobileTopBar = ({ onLogout }) => {
  const location = useLocation();
  const current = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));
  return (
    <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-neutral-200">
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-neutral-950 text-white grid place-items-center font-semibold">M</div>
          <div className="text-sm font-medium">{current?.label || "Admin"}</div>
        </div>
        <button onClick={onLogout} className="text-xs px-3 py-1.5 rounded-full bg-neutral-950 text-white">
          Logout
        </button>
      </div>
      <div className="flex gap-1 px-3 pb-2 overflow-x-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              `whitespace-nowrap text-xs px-3 py-1.5 rounded-full ${
                isActive ? "bg-neutral-950 text-white" : "bg-neutral-100 text-neutral-700"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
};

export default AdminShell;
